import type { GameInput } from "../game/input.js";

/** Input ownership follows panel visibility, including panels opened by a network reply. */
export function setupControlHud(input: GameInput, app: HTMLElement): void {
  const requireElement = (id: string): HTMLElement => {
    const element = app.querySelector<HTMLElement>(`#${id}`);
    if (!element) throw new Error(`Missing gameplay control: ${id}`);
    return element;
  };
  input.bindTouchControls(requireElement("aim-joystick"), requireElement("aim-joystick-knob"), requireElement("crouch-control"));
  const reticle = requireElement("aim-reticle");
  const resume = requireElement("aim-resume");
  const mouseLegend = requireElement("controls-mouse");
  const handLegend = requireElement("controls-main-hand");
  const update = (): void => {
    const tps = input.cameraMode === "tps";
    const blocked = input.uiBlocked;
    const fallback = input.pointerLockState === "fallback";
    app.dataset.cameraMode = input.cameraMode;
    app.dataset.inputBlocked = String(blocked);
    reticle.hidden = !tps || blocked;
    resume.hidden = !tps || blocked || input.pointerLockState === "locked";
    resume.textContent = fallback ? "DRAG TO AIM · CLICK TO RETRY" : input.pointerLockState === "requesting" ? "ACTIVATING AIM…" : "CLICK TO RESUME";
    mouseLegend.textContent = !tps ? "DRAG VIEW" : fallback ? "DRAG AIM" : "AIM";
    handLegend.hidden = !tps || fallback;
  };
  input.onControlStateChange = update;
  resume.addEventListener("click", () => input.requestPointerLock());
  const panels = new Set<HTMLElement>();
  const panelSelector = ".inventory-shell, .npc-conversation, .drone-debug-panel, .developer-panel, .developer-auth";
  const syncPanel = (panel: HTMLElement): void => {
    // Developer fly controls remain available while inspecting the developer panel.
    input.setUiBlocked(`panel:${panel.id || panel.className}`, !panel.hidden && panel.isConnected, !panel.matches(".developer-panel"));
  };
  const visibilityObserver = new MutationObserver((records) => {
    for (const record of records) syncPanel(record.target as HTMLElement);
  });
  const findPanels = (): void => {
    for (const panel of app.querySelectorAll<HTMLElement>(panelSelector)) {
      if (panels.has(panel)) continue;
      panels.add(panel);
      visibilityObserver.observe(panel, { attributes: true, attributeFilter: ["hidden"] });
      syncPanel(panel);
    }
    for (const panel of panels) if (!panel.isConnected) { syncPanel(panel); panels.delete(panel); }
  };
  // Panels are direct app children; avoid observing per-frame HUD text or character nameplate mutations.
  new MutationObserver(findPanels).observe(app, { childList: true });
  findPanels();
  update();
}
